import type {
  DispatchHistory,
  DispatchProposal,
  DispatchSettings,
  DispatchWorkspace,
  DraftWriteResult,
  OptimizeCommand,
  PublicationPreview,
  PublishCommand,
  PublishResult,
  RestoreVersionCommand,
  RevisionCommand,
  RoadBudget,
  RoadTimesEstimate,
  RoadTimesResult,
  SaveDraftCommand,
  SettingsCommand,
  VersionContent,
} from '../domain/dispatch'

/**
 * Référentiel de la répartition (Routes › Répartition). Le brouillon s'écrit en entier à chaque changement, avec la
 * révision lue (verrou optimiste); seule la publication touche les affectations des employés.
 */
export interface DispatchRepository {
  getWorkspace(season: string): Promise<DispatchWorkspace>
  saveDraft(command: SaveDraftCommand): Promise<DraftWriteResult>
  /** Remet le brouillon sur la version publiée (ou le vide sans version). */
  resetDraft(command: RevisionCommand): Promise<DraftWriteResult>
  restoreVersion(command: RestoreVersionCommand): Promise<DraftWriteResult>
  getPublicationPreview(season: string): Promise<PublicationPreview>
  publish(command: PublishCommand): Promise<PublishResult>
  /** Relance la prise d'effet d'une version en attente ou en échec (refusée pendant une opération ouverte). */
  applyPending(season: string): Promise<void>
  getHistory(season: string): Promise<DispatchHistory>
  getVersion(versionId: string): Promise<VersionContent>
  updateSettings(command: SettingsCommand): Promise<DispatchSettings>
  /** Secteur déclaré d'une route (`route.update`); un écart reste un avertissement. */
  setRouteSector(routeId: string, sectorId: string | null): Promise<void>
  /** Lot E : plafond mensuel et compteur des appels Google payants. */
  getRoadBudget(): Promise<RoadBudget>
  /** Plafond fixé par le propriétaire (`settings.manage`). */
  setRoadBudget(monthlyUnitCap: number): Promise<RoadBudget>
  /** Éléments qu'un calcul facturerait, sans rien réserver ni appeler. */
  estimateRoadTimes(season: string, scope: 'PLAN' | 'ALL_PAIRS'): Promise<RoadTimesEstimate>
  /** Durées Google des transitions du brouillon (fonction Edge; plafond vérifié avant l'appel). */
  computeRoadTimes(season: string): Promise<RoadTimesResult>
  /** Lot F : calcule une proposition de plan de tracteurs (jamais appliquée ni publiée). */
  optimize(command: OptimizeCommand): Promise<DispatchProposal>
  /** Dernière proposition en revue de la saison, ou null. */
  getProposal(season: string): Promise<DispatchProposal | null>
  /** Remplace le brouillon par la proposition, tout ou rien. */
  applyProposal(runId: string): Promise<DispatchProposal>
  dismissProposal(runId: string): Promise<DispatchProposal>
}

export class DispatchError extends Error {
  constructor(readonly code: string) {
    super(code)
    this.name = 'DispatchError'
  }
}

const messages: Record<string, string> = {
  DISPATCH_READ_FORBIDDEN: 'Vous n’avez pas accès à la répartition.',
  DISPATCH_EDIT_FORBIDDEN: 'Vous ne pouvez pas modifier le brouillon de répartition.',
  DISPATCH_PUBLISH_FORBIDDEN: 'Vous ne pouvez pas publier la répartition.',
  DISPATCH_DRAFT_CONFLICT:
    'Le brouillon a changé ailleurs. Il a été rechargé : refaites votre dernier changement.',
  DISPATCH_PUBLICATION_CONFLICT:
    'Une autre version a été publiée entre-temps. Relisez les changements avant de publier.',
  DISPATCH_PUBLICATION_BLOCKED: 'Le plan contient un problème bloquant. Corrigez-le avant de publier.',
  DISPATCH_OPERATION_OPEN: 'Une opération est ouverte : la version prendra effet à sa fermeture.',
  DISPATCH_NOTHING_PENDING: 'Aucune version en attente.',
  DISPATCH_NOTHING_TO_PUBLISH: 'Le brouillon est identique à la version publiée.',
  DISPATCH_ROUTE_LOCKED: 'Cette route est verrouillée : retirez le verrou avant de la déplacer.',
  DISPATCH_ROUTE_NOT_FOUND: 'Une route du plan n’existe plus. Le brouillon a été rechargé.',
  DISPATCH_ROUTE_DUPLICATE: 'Une route ne peut être que sur un seul tracteur.',
  DISPATCH_EQUIPMENT_DUPLICATE: 'Ce tracteur est déjà dans le plan.',
  DISPATCH_EQUIPMENT_NOT_FOUND: 'Ce tracteur n’existe plus.',
  DISPATCH_OPERATOR_DUPLICATE: 'Cet opérateur conduit déjà un autre tracteur du plan.',
  DISPATCH_OPERATOR_NOT_FOUND: 'Cet opérateur n’existe plus.',
  DISPATCH_SETTINGS_INVALID: 'Valeurs invalides : vérifiez les durées et les seuils.',
  DISPATCH_NOTE_TOO_LONG: 'La note est trop longue.',
  DISPATCH_VERSION_NOT_FOUND: 'Cette version n’existe pas.',
  DISPATCH_PLAN_NOT_FOUND: 'Aucun plan pour cette saison.',
  DISPATCH_SEASON_INVALID: 'Saison invalide.',
  ROUTE_SECTOR_NOT_FOUND: 'Ce secteur n’existe pas ou est archivé.',
  PAID_API_BUDGET_EXCEEDED:
    'Plafond mensuel des appels Google atteint : aucun appel n’a été fait. Le propriétaire peut relever le plafond.',
  PAID_API_BUDGET_FORBIDDEN: 'Seul un responsable des paramètres peut fixer le plafond.',
  PAID_API_BUDGET_INVALID: 'Plafond invalide : un nombre entier d’éléments, de 0 à 1 000 000.',
  DISPATCH_ROAD_TOO_MANY_PAIRS: 'Trop de trajets à calculer d’un coup (625 au plus).',
  DISPATCH_ROAD_REQUEST_CLOSED: 'Ce calcul est déjà terminé.',
  DISPATCH_OPTIMIZATION_TOO_FEW_ROUTES: 'Il faut au moins deux routes dans le brouillon pour optimiser.',
  DISPATCH_OPTIMIZATION_TOO_MANY_ROUTES: 'Trop de routes pour l’optimiseur (40 au plus).',
  DISPATCH_OPTIMIZATION_INVALID: 'La proposition calculée n’est pas valable. Rien n’a été changé.',
  DISPATCH_OPTIMIZATION_CLOSED: 'Cette proposition a déjà été appliquée ou écartée.',
  DISPATCH_OPTIMIZATION_NOT_FOUND: 'Cette proposition n’existe plus.',
  DISPATCH_PROPOSAL_STALE:
    'Le brouillon a changé depuis le calcul : la proposition ne s’applique plus. Relancez l’optimisation.',
  PROVIDER_NOT_CONFIGURED: 'Le service Google n’est pas configuré sur le serveur.',
  PROVIDER_AUTH: 'Google a refusé l’accès (compte de service ou API non activée).',
  PROVIDER_QUOTA: 'Quota Google atteint. Réessayez plus tard.',
  PROVIDER_TIMEOUT: 'Google n’a pas répondu à temps. Réessayez.',
  PROVIDER_REJECTED: 'Google a refusé la demande.',
  PROVIDER_ERROR: 'Erreur du service Google. Réessayez.',
  INVALID_RESULT: 'Réponse inattendue du service. Rien n’a été changé.',
  NETWORK: 'Connexion impossible. Vérifiez le réseau, puis réessayez.',
}

export function dispatchErrorMessage(error: unknown): string {
  const code = error instanceof DispatchError ? error.code : 'UNEXPECTED'
  return messages[code] ?? 'Une erreur inattendue est survenue. Réessayez.'
}
